import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, RotateCcw, ExternalLink } from "lucide-react";
import { PDFViewer } from "@react-pdf/renderer";
import { api, errMsg } from "../lib/api";
import { Panel } from "../components/shared";
import { PODocument, SAMPLE_PO, poBlob } from "../components/PODocument";

const inputCls = "h-11 w-full px-3 bg-[#090D16] border border-[#334155] rounded-md focus:outline-none focus:border-[#F59E0B] text-sm";
const L = ({ label, children }) => <label className="flex flex-col gap-1"><span className="font-mono text-[10px] tracking-[0.2em] text-[#6B7280]">{label}</span>{children}</label>;
const TEXT = [["company_name", "COMPANY NAME"], ["company_phone", "PHONE"], ["company_email", "EMAIL"], ["logo_url", "LOGO IMAGE URL (PNG/JPG)"], ["title", "DOCUMENT TITLE"], ["payment_terms", "PAYMENT TERMS"], ["notes_heading", "NOTES HEADING"]];
const AREAS = [["company_address", "COMPANY ADDRESS"], ["ship_to", "SHIP-TO ADDRESS"], ["footer_notes", "TERMS / FOOTER NOTES"]];
const TOGGLES = [["show_sku", "SKU column"], ["show_packs", "Packs column"], ["show_unit_cost", "Prices & totals"], ["show_signature", "Signature lines"]];

function useDebounced(v, ms) {
  const [d, setD] = useState(v);
  useEffect(() => { const t = setTimeout(() => setD(v), ms); return () => clearTimeout(t); }, [v, ms]);
  return d;
}

export default function POTemplate() {
  const [t, setT] = useState(null);
  const preview = useDebounced(t, 600);
  useEffect(() => { api.get("/po-template").then((r) => setT(r.data)); }, []);
  if (!t) return <div className="font-mono text-[#6B7280]">LOADING…</div>;
  const set = (k, v) => setT({ ...t, [k]: v });

  const save = async () => { try { setT((await api.put("/po-template", t)).data); toast.success("Template saved"); } catch (e) { toast.error(errMsg(e)); } };
  const reset = async () => { if (!window.confirm("Reset template to the sample defaults?")) return; setT((await api.post("/po-template/reset")).data); toast.success("Template reset"); };
  const openPdf = async () => window.open(URL.createObjectURL(await poBlob(SAMPLE_PO, t)), "_blank");

  return (
    <div>
      <div className="flex flex-wrap justify-end gap-2 mb-4">
        <button data-testid="template-open-pdf-btn" onClick={openPdf} className="h-12 px-4 rounded-md border border-[#334155] font-semibold flex items-center gap-2"><ExternalLink className="w-4 h-4" />Sample PDF</button>
        <button data-testid="template-reset-btn" onClick={reset} className="h-12 px-4 rounded-md border border-[#334155] flex items-center gap-2"><RotateCcw className="w-4 h-4" />Reset</button>
        <button data-testid="template-save-btn" onClick={save} className="h-12 px-5 rounded-md bg-[#F59E0B] text-[#090D16] font-bold flex items-center gap-2"><Save className="w-4 h-4" />Save</button>
      </div>
      <div className="grid xl:grid-cols-[420px_1fr] gap-6">
        <Panel className="p-5 space-y-4" data-testid="template-form">
          {TEXT.map(([k, l]) => <L key={k} label={l}><input data-testid={`template-${k}`} className={inputCls} value={t[k] || ""} onChange={(e) => set(k, e.target.value)} /></L>)}
          {AREAS.map(([k, l]) => <L key={k} label={l}><textarea data-testid={`template-${k}`} rows={3} className={`${inputCls} h-auto py-2`} value={t[k] || ""} onChange={(e) => set(k, e.target.value)} /></L>)}
          <div className="grid grid-cols-3 gap-3">
            <L label="ACCENT"><input type="color" data-testid="template-accent_color" value={t.accent_color} onChange={(e) => set("accent_color", e.target.value)} className="h-11 w-full bg-[#090D16] border border-[#334155] rounded-md" /></L>
            <L label="FONT"><select data-testid="template-font" className={inputCls} value={t.font} onChange={(e) => set("font", e.target.value)}>{["Helvetica", "Times-Roman", "Courier"].map((f) => <option key={f}>{f}</option>)}</select></L>
            <L label="PAPER"><select data-testid="template-paper_size" className={inputCls} value={t.paper_size} onChange={(e) => set("paper_size", e.target.value)}>{["LETTER", "A4"].map((f) => <option key={f}>{f}</option>)}</select></L>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {TOGGLES.map(([k, l]) => (
              <label key={k} className="flex items-center gap-2 h-11 cursor-pointer text-sm"><input type="checkbox" data-testid={`template-${k}`} checked={!!t[k]} onChange={(e) => set(k, e.target.checked)} className="w-5 h-5 accent-[#F59E0B]" />{l}</label>
            ))}
          </div>
        </Panel>
        <Panel className="overflow-hidden min-h-[80vh]" data-testid="template-preview">
          <PDFViewer width="100%" height="100%" style={{ minHeight: "80vh", border: 0 }} showToolbar>
            <PODocument po={SAMPLE_PO} template={preview || t} />
          </PDFViewer>
        </Panel>
      </div>
    </div>
  );
}
